import { useState, useEffect, createContext, lazy, Suspense, cloneElement, isValidElement } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { fetchVisitorCountry } from './utils/geo';
import { getTranslation, getInitialLanguage, hasManualLanguagePreference, markLanguagePreferenceManual, SUPPORTED_LANGUAGES } from './utils/i18n';
import {
  buildRatesFromSettings,
  formatMoney,
  getInitialCurrency,
  hasManualCurrencyPreference,
  markCurrencyPreferenceManual,
  SUPPORTED_CURRENCIES,
} from './utils/currency';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import FloatingAnnouncement from './components/FloatingAnnouncement';
import PageViewTracker from './components/PageViewTracker';
import AppUpdatePrompt from './components/AppUpdatePrompt';
import { initSiteAnalytics, applyGoogleSiteVerification } from './utils/analytics';
import { installNativeGoogleOAuthBridge } from './utils/googleAuth';
import NativeSessionListener from './components/NativeSessionListener';
import { isKayiComNativeApp } from './utils/androidApp';
import { installAppPullToRefresh } from './utils/appPullToRefresh';
import { userMustVerifyEmail } from './utils/emailVerification';

// Pages are code-split (React.lazy) so each route only downloads the JS it needs.
// This dramatically reduces the initial JavaScript bundle for first paint.
function EmailVerificationGate({ user, getStoredUser }) {
  const location = useLocation();
  const currentUser = user || getStoredUser();
  if (!userMustVerifyEmail(currentUser, location.pathname)) return null;
  return <Navigate to="/verify-email" replace state={{ from: location }} />;
}

const HomePage = lazy(() => import('./pages/NewHomePage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const AmlPolicyPage = lazy(() => import('./pages/AmlPolicyPage'));
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const OrderTrackingPage = lazy(() => import('./pages/OrderTrackingPage'));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'));
const PurchaseOrdersPage = lazy(() => import('./pages/PurchaseOrdersPage'));
const MySubscriptionsPage = lazy(() => import('./pages/MySubscriptionsPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/AdminProducts'));
const AdminOrders = lazy(() => import('./pages/AdminOrders'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const AdminWhatsApp = lazy(() => import('./pages/AdminWhatsApp'));
const AdminWithdrawals = lazy(() => import('./pages/AdminWithdrawals'));
const AdminWalletTopups = lazy(() => import('./pages/AdminWalletTopups'));
const AdminCoupons = lazy(() => import('./pages/AdminCoupons'));
const AdminMinutesTransfers = lazy(() => import('./pages/AdminMinutesTransfers'));
const AdminCustomers = lazy(() => import('./pages/AdminCustomers'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ReferralPage = lazy(() => import('./pages/ReferralPage'));
const WithdrawPage = lazy(() => import('./pages/WithdrawPage'));
const WalletPage = lazy(() => import('./pages/WalletPage'));
const MinutesTransferPage = lazy(() => import('./pages/MinutesTransferPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const MonCashUssdPayPage = lazy(() => import('./pages/MonCashUssdPayPage'));
const StripeCardPayPage = lazy(() => import('./pages/StripeCardPayPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));
const AdminBlog = lazy(() => import('./pages/AdminBlog'));
const AdminSellers = lazy(() => import('./pages/AdminSellers'));
const AdminProductRequests = lazy(() => import('./pages/AdminProductRequests'));
const AdminPendingProducts = lazy(() => import('./pages/AdminPendingProducts'));
const SellerApplyPage = lazy(() => import('./pages/SellerApplyPage'));
const SellerDashboard = lazy(() => import('./pages/SellerDashboard'));
const SellerStorePage = lazy(() => import('./pages/SellerStorePage'));
const DisputeCenterPage = lazy(() => import('./pages/DisputeCenterPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const MarketplacePage = lazy(() => import('./pages/MarketplacePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const GameLandingPage = lazy(() => import('./pages/GameLandingPage'));

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, ''); // Remove trailing slashes
export const API = `${BACKEND_URL}/api`;

// Create axios instance
export const axiosInstance = axios.create({
  baseURL: API,
  timeout: 90000,
});

// Read the persisted auth token (stored inside the 'user' object by login()).
const getStoredAuthToken = () => {
  const read = (storage) => {
    try {
      const raw = storage.getItem('user');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.access_token || null;
    } catch (e) {
      return null;
    }
  };
  return read(localStorage) || read(sessionStorage);
};

// Attach the bearer token to every backend request when logged in.
axiosInstance.interceptors.request.use((config) => {
  const token = getStoredAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On an expired/invalid KayiCom session, clear it and send the user to login.
// Do NOT treat provider/proxy 401s (e.g. G2Bulk Invalid api_key) as logout.
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const detail = error?.response?.data?.detail;
      const detailText = typeof detail === 'string' ? detail : '';
      const isSessionAuthFailure =
        !detailText
        || /authentication required|invalid credentials|invalid or missing bearer|invalid token|token expired|not authenticated/i.test(detailText);
      const isProviderError = /g2bulk|plisio|bazik|5sim|natcash|plopplop|provider/i.test(detailText);
      const reqUrl = String(error?.config?.url || '');
      const isPlopPlopCall = /\/payments\/plopplop\//i.test(reqUrl);
      if (isSessionAuthFailure && !isProviderError && !isPlopPlopCall) {
        try {
          localStorage.removeItem('user');
          sessionStorage.removeItem('user');
        } catch (e) { /* ignore */ }
        const path = window.location.pathname || '';
        const isPublicAuthPage = path.startsWith('/login') || path.startsWith('/register')
          || path.startsWith('/forgot-password') || path.startsWith('/reset-password')
          || path.startsWith('/payment-success')
          || path.startsWith('/pay/ussd');
        if (!isPublicAuthPage) {
          window.location.assign('/login');
        }
      }
    }
    return Promise.reject(error);
  }
);

// Create language context
export const LanguageContext = createContext();
export const CurrencyContext = createContext();

function LegacyWalletPayRedirect() {
  const { id } = useParams();
  return <Navigate to={`/pay/card/${id || ''}`} replace />;
}

function LegacyOrderTrackRedirect() {
  const { orderId } = useParams();
  return <Navigate to={`/track/${orderId || ''}`} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Ensure each route starts at the top
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState([]);
  const [settings, setSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [language, setLanguage] = useState(getInitialLanguage);
  const [currency, setCurrency] = useState('USD');
  const [currencyRates, setCurrencyRates] = useState({ USD: 1 });
  const [loading, setLoading] = useState(true); // Add loading state

  const getStoredUser = () => {
    const readUser = (storage) => {
      const raw = storage.getItem('user');
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch (e) {
        console.error('Error parsing saved user:', e);
        storage.removeItem('user');
        return null;
      }
    };
    const stored = readUser(localStorage) || readUser(sessionStorage);
    if (stored && !stored.access_token) {
      try {
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
      } catch (e) { /* ignore */ }
      return null;
    }
    return stored;
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    // Load user from storage (local or session)
    const savedUser = getStoredUser();
    if (savedUser) {
      setUser(savedUser);
      axiosInstance.get('/auth/me').then((res) => {
        const verified = res?.data?.email_verified;
        if (typeof verified !== 'boolean') return;
        const persist = (storage) => {
          try {
            const raw = storage.getItem('user');
            if (!raw) return false;
            const parsed = JSON.parse(raw);
            if (parsed.email_verified === verified) return true;
            parsed.email_verified = verified;
            storage.setItem('user', JSON.stringify(parsed));
            return true;
          } catch (e) {
            return false;
          }
        };
        if (persist(localStorage) || persist(sessionStorage)) {
          setUser((prev) => (prev ? { ...prev, email_verified: verified } : prev));
        }
      }).catch((err) => {
        if (err?.response?.status === 401) {
          try {
            localStorage.removeItem('user');
            sessionStorage.removeItem('user');
          } catch (e) { /* ignore */ }
          setUser(null);
        }
      });
    }
    setLoading(false); // Mark loading complete

    // Load cart from localStorage
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }

    // Load site settings
    loadSettings();
  }, []);

  useEffect(() => {
    // Android shell: pull-to-refresh on every route (SPA-safe).
    if (!isKayiComNativeApp()) return undefined;
    return installAppPullToRefresh();
  }, []);

  useEffect(() => {
    // Crisp chat widget is heavy in the Android WebView — keep WhatsApp/Help instead.
    if (isKayiComNativeApp()) {
      const existingScript = document.getElementById('crisp-chat-script');
      if (existingScript) existingScript.remove();
      return undefined;
    }
    const crispId = (settings?.crisp_website_id || '').trim();
    const enabled = Boolean(settings?.crisp_enabled && crispId);
    const existingScript = document.getElementById('crisp-chat-script');
    const currentId = window.CRISP_WEBSITE_ID;

    if (enabled) {
      if (existingScript && currentId === crispId) {
        return;
      }
      if (existingScript) {
        existingScript.remove();
      }
      window.$crisp = window.$crisp || [];
      window.CRISP_WEBSITE_ID = crispId;
      const script = document.createElement('script');
      script.id = 'crisp-chat-script';
      script.src = 'https://client.crisp.chat/l.js';
      script.async = true;
      document.body.appendChild(script);
    } else {
      if (existingScript) {
        existingScript.remove();
      }
      if (window.$crisp) {
        try {
          window.$crisp.push(['do', 'chat:hide']);
        } catch (e) {
          // no-op
        }
      }
      if (window.$crisp) delete window.$crisp;
      if (window.CRISP_WEBSITE_ID) delete window.CRISP_WEBSITE_ID;
    }
  }, [settings?.crisp_enabled, settings?.crisp_website_id]);

  useEffect(() => {
    if (!settingsLoaded) return;
    initSiteAnalytics(settings?.google_analytics_id);
    applyGoogleSiteVerification(settings?.google_site_verification);
  }, [settingsLoaded, settings?.google_analytics_id, settings?.google_site_verification]);

  const loadSettings = async () => {
    const savedUser = getStoredUser();
    const phone = savedUser?.seller_phone || null;
    try {
      const [settingsResult, geoCountry] = await Promise.allSettled([
        axiosInstance.get('/settings'),
        fetchVisitorCountry(),
      ]);
      const data = settingsResult.status === 'fulfilled' ? settingsResult.value.data : null;
      const geoOptions = {
        countryCode: geoCountry.status === 'fulfilled' ? geoCountry.value : null,
        phone,
      };

      if (data) {
        setSettings(data);
        const rates = buildRatesFromSettings(data);
        setCurrencyRates(rates);
      }
      if (!hasManualCurrencyPreference()) {
        setCurrency(getInitialCurrency(data, { ...geoOptions, geoResolved: true }));
      }
      if (!hasManualLanguagePreference()) {
        setLanguage(getInitialLanguage(geoOptions));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setSettingsLoaded(true);
    }
  };

  const login = (userData, options = {}) => {
    const { remember = true } = options;
    const normalized = { ...(userData || {}) };
    // Add id field for consistency (backend sends user_id)
    if (normalized.user_id && !normalized.id) {
      normalized.id = normalized.user_id;
    }
    // Normalize name field used by dashboards/UI
    if (!normalized.full_name && normalized.username) {
      normalized.full_name = normalized.username;
    }
    setUser(normalized);
    const storage = remember ? localStorage : sessionStorage;
    storage.setItem('user', JSON.stringify(normalized));
    (remember ? sessionStorage : localStorage).removeItem('user');
    return normalized; // Return for redirect logic
  };

  // Android App Link return from Google Custom Tabs → finish login even if
  // LoginPage is not mounted yet (avoids "back in app but login never completes").
  useEffect(() => {
    const uninstall = installNativeGoogleOAuthBridge({
      settings,
      postCode: async ({ code, redirect_uri }) => {
        const response = await axiosInstance.post('/auth/login/google/code', {
          code,
          redirect_uri,
        });
        return response.data;
      },
      onSuccess: (userData) => {
        login(userData, { remember: true });
        toast.success(getTranslation(language, 'loginSuccess') || 'Login successful');
        const dest = userData?.role === 'admin' ? '/admin' : '/dashboard';
        try {
          window.__kayicomNativeSessionHandled = true;
          window.dispatchEvent(new CustomEvent('kayicom-native-session', {
            detail: { user: userData, dest },
          }));
        } catch {
          window.location.replace(dest);
        }
      },
      onError: (error) => {
        const detail = error?.response?.data?.detail || error?.message;
        toast.error(typeof detail === 'string' ? detail : (getTranslation(language, 'error') || 'Error'));
      },
    });
    return uninstall;
  }, [settings, language]);

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    const t = (key) => getTranslation(language, key);
    toast.success(t('logoutSuccess'));
  };

  const switchLanguage = (lang) => {
    const next = SUPPORTED_LANGUAGES.includes(lang) ? lang : 'en';
    setLanguage(next);
    markLanguagePreferenceManual(next);
  };

  const switchCurrency = (code) => {
    const next = SUPPORTED_CURRENCIES.includes(code) ? code : 'USD';
    setCurrency(next);
    markCurrencyPreferenceManual(next);
  };

  const formatPrice = (usdAmount) => formatMoney(usdAmount, currency, currencyRates);

  const t = (key) => getTranslation(language, key);
  const tt = (key, vars = {}) => {
    let text = t(key);
    Object.entries(vars).forEach(([k, v]) => {
      text = text.split(`{${k}}`).join(String(v));
    });
    return text;
  };

  const addToCart = (product, quantity = 1, extras = {}) => {
    const {
      player_id: playerId,
      server_id: serverId,
      credentials: itemCredentials,
      fivesim_country: fivesimCountry,
      fivesim_operator: fivesimOperator,
      fivesim_product: fivesimProduct,
      fivesim_requested_product: fivesimRequestedProduct,
      fivesim_quoted_price: fivesimQuotedPrice,
    } = extras || {};
    const existingItem = cart.find(item => item.product.id === product.id);
    let newCart;
    
    if (existingItem) {
      newCart = cart.map(item =>
        item.product.id === product.id
          ? {
              ...item,
              quantity: item.quantity + quantity,
              ...(playerId ? { player_id: playerId } : {}),
              ...(serverId ? { server_id: serverId } : {}),
              ...(itemCredentials ? { credentials: itemCredentials } : {}),
              ...(fivesimCountry ? { fivesim_country: fivesimCountry } : {}),
              ...(fivesimOperator ? { fivesim_operator: fivesimOperator } : {}),
              ...(fivesimProduct ? { fivesim_product: fivesimProduct } : {}),
              ...(fivesimRequestedProduct ? { fivesim_requested_product: fivesimRequestedProduct } : {}),
              ...(fivesimQuotedPrice != null ? { fivesim_quoted_price: fivesimQuotedPrice } : {}),
            }
          : item
      );
    } else {
      newCart = [...cart, {
        product,
        quantity,
        ...(playerId ? { player_id: playerId } : {}),
        ...(serverId ? { server_id: serverId } : {}),
        ...(itemCredentials ? { credentials: itemCredentials } : {}),
        ...(fivesimCountry ? { fivesim_country: fivesimCountry } : {}),
        ...(fivesimOperator ? { fivesim_operator: fivesimOperator } : {}),
        ...(fivesimProduct ? { fivesim_product: fivesimProduct } : {}),
        ...(fivesimRequestedProduct ? { fivesim_requested_product: fivesimRequestedProduct } : {}),
        ...(fivesimQuotedPrice != null ? { fivesim_quoted_price: fivesimQuotedPrice } : {}),
      }];
    }
    
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    toast.success(t('productAdded'));
  };

  const removeFromCart = (productId) => {
    const newCart = cart.filter(item => item.product.id !== productId);
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    toast.success(t('productRemoved'));
  };

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    const newCart = cart.map(item =>
      item.product.id === productId ? { ...item, quantity } : item
    );
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('cart');
  };

  const ProtectedRoute = ({ children, adminOnly = false }) => {
    const location = useLocation();
    // Show loading while checking authentication
    if (loading) {
      return (
        <div className="min-h-screen gradient-bg flex items-center justify-center">
          <div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-green-400 animate-spin" />
        </div>
      );
    }

    // Check storage as fallback if user state is null
    const savedUser = !user ? getStoredUser() : null;
    const currentUser = user || savedUser;
    
    if (!currentUser) {
      return <Navigate to="/login" replace state={{ from: location }} />;
    }
    
    if (adminOnly && currentUser.role !== 'admin') {
      return <Navigate to="/" replace />;
    }

    if (userMustVerifyEmail(currentUser, location.pathname)) {
      return <Navigate to="/verify-email" replace state={{ from: location }} />;
    }

    if (isValidElement(children)) {
      return cloneElement(children, { user: currentUser, authReady: true });
    }

    return children;
  };

  return (
    <LanguageContext.Provider value={{ language, switchLanguage, t, tt }}>
      <CurrencyContext.Provider value={{ currency, switchCurrency, formatPrice, currencyRates }}>
      <BrowserRouter>
        <ScrollToTop />
        <EmailVerificationGate user={user} getStoredUser={getStoredUser} />
        <NativeSessionListener login={login} />
        <PageViewTracker settings={settings} settingsLoaded={settingsLoaded} />
        <div className="App">
          <Suspense
            fallback={
              <div className="min-h-screen gradient-bg flex items-center justify-center">
                <div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-green-400 animate-spin" />
              </div>
            }
          >
          <Routes>
            <Route path="/" element={<HomePage user={user} logout={logout} cart={cart} settings={settings} />} />
            {/* Capacitor used to boot on /app.html — never show the 404 page for that path. */}
            <Route path="/app.html" element={<Navigate to="/" replace />} />
          <Route path="/marketplace" element={<MarketplacePage user={user} logout={logout} addToCart={addToCart} cart={cart} settings={settings} />} />
          <Route path="/products" element={<ProductsPage user={user} logout={logout} addToCart={addToCart} cart={cart} settings={settings} />} />
          <Route path="/products/:category" element={<ProductsPage user={user} logout={logout} addToCart={addToCart} cart={cart} settings={settings} />} />
          <Route path="/product/:slug" element={<ProductDetailPage user={user} logout={logout} addToCart={addToCart} cart={cart} settings={settings} />} />
          <Route path="/cart" element={<CartPage user={user} logout={logout} cart={cart} removeFromCart={removeFromCart} updateCartQuantity={updateCartQuantity} settings={settings} />} />
          <Route path="/checkout" element={<CheckoutPage user={user} logout={logout} cart={cart} clearCart={clearCart} settings={settings} />} />
          <Route path="/refund-policy" element={<RefundPolicy user={user} logout={logout} cart={cart} settings={settings} />} />
          <Route path="/aml-policy" element={<AmlPolicyPage user={user} logout={logout} cart={cart} settings={settings} />} />
          <Route path="/help" element={<HelpCenterPage user={user} logout={logout} settings={settings} cart={cart} />} />
          <Route path="/how-it-works" element={<HowItWorksPage user={user} logout={logout} cart={cart} settings={settings} />} />
          <Route path="/buy/:slug" element={<GameLandingPage user={user} logout={logout} cart={cart} settings={settings} />} />
          <Route path="/terms" element={<TermsPage user={user} logout={logout} settings={settings} cart={cart} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage user={user} logout={logout} settings={settings} cart={cart} />} />
          <Route path="/blog" element={<BlogPage user={user} logout={logout} cart={cart} settings={settings} />} />
          <Route path="/blog/:slug" element={<BlogDetailPage user={user} logout={logout} cart={cart} settings={settings} />} />
            <Route path="/track/:orderId" element={<OrderTrackingPage user={user} logout={logout} settings={settings} />} />
            <Route path="/order/:orderId" element={<LegacyOrderTrackRedirect />} />
            <Route path="/crypto" element={<Navigate to="/wallet" replace />} />
            <Route path="/admin/crypto" element={<Navigate to="/admin" replace />} />
            <Route path="/admin/crypto-transactions" element={<Navigate to="/admin" replace />} />
          <Route path="/pay/ussd/:id" element={<MonCashUssdPayPage user={user} logout={logout} settings={settings} />} />
          <Route path="/pay/card/:id" element={<ProtectedRoute><StripeCardPayPage user={user} logout={logout} settings={settings} clearCart={clearCart} /></ProtectedRoute>} />
          <Route path="/pay/apple-pay/:id" element={<LegacyWalletPayRedirect />} />
          <Route path="/pay/google-pay/:id" element={<LegacyWalletPayRedirect />} />
          <Route path="/payment-success" element={<PaymentSuccessPage user={user} logout={logout} settings={settings} />} />
          <Route path="/login" element={<LoginPage login={login} settings={settings} />} />
          <Route path="/register" element={<RegisterPage login={login} settings={settings} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage settings={settings} />} />
          <Route path="/reset-password" element={<ResetPasswordPage settings={settings} />} />
          <Route path="/verify-email" element={<VerifyEmailPage settings={settings} user={user} login={login} />} />
          
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <CustomerDashboard user={user} logout={logout} settings={settings} cart={cart} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <PurchaseOrdersPage user={user} logout={logout} settings={settings} cart={cart} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <ProtectedRoute>
                <MySubscriptionsPage user={user} logout={logout} settings={settings} cart={cart} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/referral"
            element={
              <ReferralPage user={user} logout={logout} settings={settings} cart={cart} />
            }
          />
          
          <Route
            path="/withdraw"
            element={
              <ProtectedRoute>
                <WithdrawPage user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/wallet"
            element={
              <ProtectedRoute>
                <WalletPage user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/minutes"
            element={
              <ProtectedRoute>
                <MinutesTransferPage user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/mobile-topup"
            element={
              <ProtectedRoute>
                <MinutesTransferPage user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/seller/apply"
            element={
              <ProtectedRoute>
                <SellerApplyPage user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/seller"
            element={
              <ProtectedRoute>
                {user?.seller_status === 'approved' || user?.role === 'seller'
                  ? <SellerDashboard user={user} logout={logout} settings={settings} />
                  : <SellerApplyPage user={user} logout={logout} settings={settings} />
                }
              </ProtectedRoute>
            }
          />

          <Route
            path="/disputes"
            element={
              <ProtectedRoute>
                <DisputeCenterPage user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <MessagesPage user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboard user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/products"
            element={
              <ProtectedRoute adminOnly>
                <AdminProducts user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/orders"
            element={
              <ProtectedRoute adminOnly>
                <AdminOrders user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute adminOnly>
                <AdminSettings user={user} logout={logout} settings={settings} loadSettings={loadSettings} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/whatsapp"
            element={
              <ProtectedRoute adminOnly>
                <AdminWhatsApp user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/withdrawals"
            element={
              <ProtectedRoute adminOnly>
                <div className="min-h-screen bg-[#070b12]">
                  <Navbar user={user} logout={logout} cartItemCount={0} settings={settings} />
                  <div className="container mx-auto px-4 py-10">
                    <AdminWithdrawals />
                  </div>
                  <Footer settings={settings} />
                </div>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/wallet-topups"
            element={
              <ProtectedRoute adminOnly>
                <AdminWalletTopups user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/coupons"
            element={
              <ProtectedRoute adminOnly>
                <AdminCoupons user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/minutes-transfers"
            element={
              <ProtectedRoute adminOnly>
                <AdminMinutesTransfers user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/mobile-topups"
            element={
              <ProtectedRoute adminOnly>
                <AdminMinutesTransfers user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/customers"
            element={
              <ProtectedRoute adminOnly>
                <AdminCustomers user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blog"
            element={
              <ProtectedRoute adminOnly>
                <AdminBlog user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/sellers"
            element={
              <ProtectedRoute adminOnly>
                <AdminSellers user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/product-requests"
            element={
              <ProtectedRoute adminOnly>
                <AdminProductRequests user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/pending-products"
            element={
              <ProtectedRoute adminOnly>
                <AdminPendingProducts user={user} logout={logout} settings={settings} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/store/:sellerId"
            element={
              <SellerStorePage user={user} logout={logout} settings={settings} addToCart={addToCart} />
            }
          />

          <Route
            path="*"
            element={<NotFoundPage user={user} logout={logout} cart={cart} settings={settings} />}
          />
        </Routes>
          </Suspense>
        <Toaster position="top-right" richColors />
        <AppUpdatePrompt />
        <FloatingAnnouncement settings={settings} />
        <WhatsAppButton settings={settings} />
        </div>
      </BrowserRouter>
      </CurrencyContext.Provider>
    </LanguageContext.Provider>
  );
}

export default App;
